import {
  theme as base,
  extendTheme,
  withDefaultVariant,
} from "@chakra-ui/react";

export const CHART_HEIGHT = 320;

export type ClientType = "VAIRITAS" | "JONES";

export const clientUrlParts: Record<ClientType, string[]> = {
  VAIRITAS: ["localhost"],
  JONES: ["jones"],
};

export const ACCENT_COLORS: Record<ClientType, string> = {
  VAIRITAS: "#0291a3",
  JONES: "#c8a74d",
};
export const INIT_COLOR_MODE: Record<ClientType, "system" | "dark" | "light"> =
  {
    VAIRITAS: "system",
    JONES: "dark",
  };

export const FAVICONS: Record<ClientType, string> = {
  VAIRITAS: "vairitasFavicon.png",
  JONES: "jonesFavicon.ico",
};

export const SPLASH_SIDE_BG: Record<ClientType, string> = {
  VAIRITAS: "blue.300",
  JONES: "blue.300",
};

export const NAV_ICON_FILL: Record<ClientType, string> = {
  VAIRITAS: "#848688",
  JONES: "#fff",
};

export const NAV_DARK_SUBMENU_FILL: Record<ClientType, string> = {
  VAIRITAS: "#fff",
  JONES: "#fff",
};

export const NAV_DARK_HOVER_BG: Record<ClientType, string> = {
  VAIRITAS: "#ffffff29",
  JONES: "#ffffff29",
};

export const MAIN_BG_DARK: Record<ClientType, string> = {
  VAIRITAS: "#1a202c",
  JONES: "#000000",
};

export function getClientByHostname(hostname: string): ClientType {
  // return "JONES";
  for (const key of Object.keys(clientUrlParts) as ClientType[]) {
    if (clientUrlParts[key].some((part) => hostname.includes(part))) {
      return key;
    }
  }
  return "VAIRITAS";
}

export function getChakraTheme(clientType: ClientType) {
  const accentColor = ACCENT_COLORS[clientType];
  const initialColorMode = INIT_COLOR_MODE[clientType];

  return extendTheme(
    {
      initialColorMode,
      colors: {
        accentColor,
        darkBG: "#111E34",
        fieldBgLight: "#F8FAFC",
        fieldBgDark: "#040016",
        lightBorder: "#1B1B36",
        darkBorder: "#78789C",
        darkRed: "#dc2626",
        lightRed: "#f87171",
      },
      components: {
        Button: {
          baseStyle: {
            borderRadius: 0,
          },
          variants: {
            solid: {
              borderRadius: 0,
            },
            outline: {
              borderRadius: 0,
            },
            ghost: {
              borderRadius: 0,
            },
            link: {
              borderRadius: 0,
            },
            unstyled: {
              borderRadius: 0,
            },
          },
          sizes: {
            xs: { borderRadius: 0 },
            sm: { borderRadius: 0 },
            md: { borderRadius: 0 },
            lg: { borderRadius: 0 },
            xl: { borderRadius: 0 },
          },
        },
        IconButton: {
          baseStyle: {
            borderRadius: 0,
          },
          variants: {
            solid: { borderRadius: 0 },
            outline: { borderRadius: 0 },
            ghost: { borderRadius: 0 },
          },
          sizes: {
            xs: { borderRadius: 0 },
            sm: { borderRadius: 0 },
            md: { borderRadius: 0 },
            lg: { borderRadius: 0 },
            xl: { borderRadius: 0 },
          },
        },
        Input: {
          baseStyle: {
            field: {
              borderRadius: 0,
            },
          },
          variants: {
            outline: {
              field: {
                borderRadius: 0,
              },
            },
            filled: {
              field: {
                borderRadius: 0,
              },
            },
            flushed: {
              field: {
                borderRadius: 0,
              },
            },
          },
        },
        Textarea: {
          baseStyle: {
            borderRadius: 0,
          },
        },
        Select: {
          baseStyle: {
            field: {
              borderRadius: 0,
            },
          },
        },
      },
    },
    withDefaultVariant({
      variant: "filled",
      components: ["Input"],
    })
  );
}

const theme = extendTheme(
  {
    initialColorMode: "system",
    colors: {
      darkBG: "#111E34",
      fieldBgLight: "#F8FAFC",
      fieldBgDark: "#040016",
      lightBorder: "#1B1B36",
      darkBorder: "#78789C",

      appGreen: "#22C55E",
      appRed: "#EF4444",
      appYellow: "#EAB308",
      appGray: "#6B7280",
      appBlue: "#3182ce",
    },
    fonts: {
      heading: `Inter, ${base.fonts.heading}`,
      body: `Inter, ${base.fonts.body}`,
    },
    styles: {
      global: {
        // Force all buttons to have no border radius
        'button, [role="button"]': {
          borderRadius: "0 !important",
        },
        // Specifically target Chakra UI buttons
        'button[data-chakra-component="Button"], button[data-chakra-component="IconButton"]':
          {
            borderRadius: "0 !important",
          },
        // Force all inputs to have no border radius
        "input, textarea, select": {
          borderRadius: "0 !important",
        },
        // Specifically target Chakra UI inputs
        'input[data-chakra-component="Input"], textarea[data-chakra-component="Textarea"], select[data-chakra-component="Select"]':
          {
            borderRadius: "0 !important",
          },
      },
    },
    components: {
      Button: {
        baseStyle: {
          borderRadius: 0, // Use number instead of string
        },
        variants: {
          solid: {
            borderRadius: 0,
          },
          outline: {
            borderRadius: 0,
          },
          ghost: {
            borderRadius: 0,
          },
          link: {
            borderRadius: 0,
          },
          unstyled: {
            borderRadius: 0,
          },
        },
        // Override all sizes to ensure borderRadius is 0
        sizes: {
          xs: {
            borderRadius: 0,
          },
          sm: {
            borderRadius: 0,
          },
          md: {
            borderRadius: 0,
          },
          lg: {
            borderRadius: 0,
          },
          xl: {
            borderRadius: 0,
          },
        },
      },
      Card: {
        baseStyle: {
          container: {
            borderRadius: "none", // or 0
            shadow: "none",
          },
          body: {
            borderRadius: "none", // or 0
            shadow: "none",
          },
        },
      },
      IconButton: {
        baseStyle: {
          borderRadius: 0,
        },
        variants: {
          solid: {
            borderRadius: 0,
          },
          outline: {
            borderRadius: 0,
          },
          ghost: {
            borderRadius: 0,
          },
        },
        sizes: {
          xs: {
            borderRadius: 0,
          },
          sm: {
            borderRadius: 0,
          },
          md: {
            borderRadius: 0,
          },
          lg: {
            borderRadius: 0,
          },
          xl: {
            borderRadius: 0,
          },
        },
      },
      Input: {
        baseStyle: {
          field: {
            borderRadius: 0,
            border: "1px solid",
            backgroundColor: "#ffffff",
            fontSize: "14px",
            lineHeight: "22px",
            padding: "4px 11px",

            transition: "all 0.2s",
            _hover: {
              borderColor: "#4096ff",
            },
            _focus: {
              borderColor: "#4096ff",
              boxShadow: "0 0 0 2px rgba(5, 145, 255, 0.1)",
              outline: "none",
            },

            _disabled: {
              cursor: "not-allowed",
            },
            _invalid: {
              borderColor: "#ff4d4f",
              _focus: {
                borderColor: "#ff4d4f",
                boxShadow: "0 0 0 2px rgba(255, 77, 79, 0.1)",
              },
            },
          },
        },
        variants: {
          outline: {
            field: {
              borderRadius: 0,
              border: "1px solid",
              _hover: {
                borderColor: "#4096ff",
              },
              _focus: {
                borderColor: "#4096ff",
                boxShadow: "0 0 0 2px rgba(5, 145, 255, 0.1)",
              },
            },
          },
          filled: {
            field: {
              borderRadius: 0,
              border: "1px solid",
              _hover: {
                borderColor: "#4096ff",
              },
              _focus: {
                borderColor: "#4096ff",
                boxShadow: "0 0 0 2px rgba(5, 145, 255, 0.1)",
              },
            },
          },
          flushed: {
            field: {
              borderRadius: 0,
              border: "none",
              borderBottom: "1px solid",
              borderBottomColor: "#d9d9d9",
              backgroundColor: "transparent",
              _hover: {
                borderBottomColor: "#4096ff",
              },
              _focus: {
                borderBottomColor: "#4096ff",
                boxShadow: "0 1px 0 0 #4096ff",
              },
            },
          },
        },
      },
      Textarea: {
        baseStyle: {
          borderRadius: 0,
          border: "1px solid",
          fontSize: "14px",
          lineHeight: "22px",
          padding: "4px 11px",
          transition: "all 0.2s",
          resize: "vertical",
          _hover: {
            borderColor: "#4096ff",
          },
          _focus: {
            borderColor: "#4096ff",
            boxShadow: "0 0 0 2px rgba(5, 145, 255, 0.1)",
            outline: "none",
          },

          _disabled: {
            cursor: "not-allowed",
          },
          _invalid: {
            borderColor: "#ff4d4f",
            _focus: {
              borderColor: "#ff4d4f",
              boxShadow: "0 0 0 2px rgba(255, 77, 79, 0.1)",
            },
          },
        },
      },
      Select: {
        baseStyle: {
          field: {
            borderRadius: 0,
            border: "1px solid",
            // backgroundColor: "#ffffff",
            fontSize: "14px",
            lineHeight: "22px",
            padding: "4px 11px",
            transition: "all 0.2s",
            _hover: {
              borderColor: "#4096ff",
            },
            _focus: {
              borderColor: "#4096ff",
              boxShadow: "0 0 0 2px rgba(5, 145, 255, 0.1)",
              outline: "none",
            },
            _disabled: {
              cursor: "not-allowed",
            },
            _invalid: {
              borderColor: "#ff4d4f",
              _focus: {
                borderColor: "#ff4d4f",
                boxShadow: "0 0 0 2px rgba(255, 77, 79, 0.1)",
              },
            },
          },
        },
      },
      NumberInput: {
        baseStyle: {
          field: {
            borderRadius: 0,
          },
        },
      },
      PinInput: {
        baseStyle: {
          borderRadius: 0,
        },
      },
    },
  },
  withDefaultVariant({
    variant: "filled",
    components: ["Input"],
  })
);

export default theme;
